import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header, Button, Input } from '@tadagram/ui';
import { Trash2, ChevronLeft, ChevronRight, Loader, FolderPlus, Pencil, Plus, X } from 'lucide-react';
import {
  SeedingContentsAPI,
  SeedingContentFolderAPI,
  SeedingContentFolderItem,
  SeedingContentItem,
} from './WorkflowBuilder/api';
import { useAccounts } from './WorkflowBuilder/hooks/useAccounts';

/**
 * Seeding Contents Management Page
 * Allows users to view, filter, and delete seeding content items
 */
export const SeedingContentsManagement: React.FC = () => {
  const { t } = useTranslation('seedingContents');
  const { accounts, loading: accountsLoading } = useAccounts('facebook');
  const [activeTab, setActiveTab] = useState<'contents' | 'folders'>('contents');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());

  const [contents, setContents] = useState<SeedingContentItem[]>([]);
  const [folders, setFolders] = useState<SeedingContentFolderItem[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderDescription, setFolderDescription] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [savingFolder, setSavingFolder] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadContents();
  }, [currentPage, pageSize, selectedAccountId, selectedFolderId, searchText]);

  useEffect(() => {
    loadFolders();
  }, []);

  const loadContents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await SeedingContentsAPI.listAll(currentPage, pageSize, {
        account_id: selectedAccountId || undefined,
        folder_id: selectedFolderId || undefined,
      });
      let filtered = response.contents;

      // Filter by search text
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.post_url.toLowerCase().includes(searchLower) ||
            (item.content_snippet && item.content_snippet.toLowerCase().includes(searchLower)) ||
            (item.account_id || '').toLowerCase().includes(searchLower) ||
            (item.fanpage_page_id || '').toLowerCase().includes(searchLower),
        );
      }

      setContents(filtered);
      setTotal(searchText ? filtered.length : response.total);
    } catch (err: any) {
      console.error('Failed to load seeding contents:', err);
      setError(err.message || 'Failed to load seeding contents');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      setFoldersLoading(true);
      const response = await SeedingContentFolderAPI.listAll(1, 100);
      setFolders(response.folders || []);
    } catch (err: any) {
      console.error('Failed to load seeding folders:', err);
      setError(err.message || 'Failed to load seeding folders');
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm(t('deleteItemConfirm'))) {
      return;
    }

    try {
      setDeleting((prev) => new Set([...prev, itemId]));
      await SeedingContentsAPI.deleteItem(itemId);
      setSuccessMessage(t('deleteItemSuccess'));
      setTimeout(() => setSuccessMessage(null), 2500);
      await loadContents();
    } catch (err: any) {
      console.error('Failed to delete item:', err);
      setError(t('deleteItemFailed') + ': ' + (err.message || ''));
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const resetFolderForm = () => {
    setFolderName('');
    setFolderDescription('');
    setEditingFolderId(null);
  };

  const handleEditFolder = (folder: SeedingContentFolderItem) => {
    setActiveTab('folders');
    setEditingFolderId(folder._id);
    setFolderName(folder.name || '');
    setFolderDescription(folder.description || '');
  };

  const handleSaveFolder = async () => {
    if (!folderName.trim()) {
      setError(t('folderNameRequired'));
      return;
    }
    try {
      setSavingFolder(true);
      setError(null);
      if (editingFolderId) {
        await SeedingContentFolderAPI.update(editingFolderId, {
          name: folderName.trim(),
          description: folderDescription.trim(),
        });
        setSuccessMessage(t('folderUpdateSuccess'));
      } else {
        await SeedingContentFolderAPI.create({
          name: folderName.trim(),
          description: folderDescription.trim() || undefined,
        });
        setSuccessMessage(t('folderCreateSuccess'));
      }
      resetFolderForm();
      await loadFolders();
      setTimeout(() => setSuccessMessage(null), 2500);
    } catch (err: any) {
      console.error('Failed to save folder:', err);
      setError(err.message || 'Failed to save folder');
    } finally {
      setSavingFolder(false);
    }
  };

  const handleDeleteFolder = async (folder: SeedingContentFolderItem) => {
    if (folder.is_default) {
      setError(t('folderDeleteFailed'));
      return;
    }
    if (!window.confirm(t('folderDeleteConfirm', { name: folder.name }))) {
      return;
    }
    try {
      await SeedingContentFolderAPI.delete(folder._id);
      setSuccessMessage(t('folderDeleteSuccess'));
      if (editingFolderId === folder._id) {
        resetFolderForm();
      }
      await loadFolders();
      setTimeout(() => setSuccessMessage(null), 2500);
    } catch (err: any) {
      console.error('Failed to delete folder:', err);
      setError(err.message || 'Failed to delete folder');
    }
  };

  // ── Manual add states
  const [showAddContentModal, setShowAddContentModal] = useState(false);
  const [addContentLoading, setAddContentLoading] = useState(false);
  const [addContentError, setAddContentError] = useState<string | null>(null);
  const [addContentForm, setAddContentForm] = useState({ post_url: '', account_id: '', platform: 'facebook' as 'facebook' | 'instagram' | 'tiktok' | 'linkedin', post_type: 'group' as 'group' | 'profile' | 'fanpage', target_url: '', content_snippet: '' });

  // ── Manual add handler
  const handleManualAddContent = async () => {
    if (!addContentForm.post_url.trim()) {
      setAddContentError('Vui lòng nhập URL bài đăng');
      return;
    }
    setAddContentLoading(true);
    setAddContentError(null);
    try {
      await SeedingContentsAPI.create({
        post_url: addContentForm.post_url,
        platform: addContentForm.platform,
        account_id: addContentForm.account_id || undefined,
        post_type: addContentForm.post_type,
        target_url: addContentForm.target_url || undefined,
        content_snippet: addContentForm.content_snippet || undefined,
      });
      setShowAddContentModal(false);
      setAddContentForm({ post_url: '', account_id: '', platform: 'facebook', post_type: 'group', target_url: '', content_snippet: '' });
      setCurrentPage(1);
      loadContents();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setAddContentError(e?.response?.data?.message || 'Lỗi khi thêm, thử lại sau');
    } finally {
      setAddContentLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, total);
  const folderNameById = folders.reduce<Record<string, string>>((acc, folder) => {
    acc[folder._id] = folder.name;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-tg-bg pb-20">
      <Header title={t('pageTitle')} subtitle={t('pageSubtitle')} />

      <div className="container max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('contents')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'contents'
                ? 'bg-tg-primary text-white'
                : 'bg-white text-gray-700 border border-gray-200'
            }`}
          >
            {t('tabContents')}
          </button>
          <button
            onClick={() => setActiveTab('folders')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'folders'
                ? 'bg-tg-primary text-white'
                : 'bg-white text-gray-700 border border-gray-200'
            }`}
          >
            {t('tabFolders')}
          </button>
          {activeTab === 'contents' && (
            <button
              onClick={() => setShowAddContentModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors ml-auto"
            >
              <Plus size={14} />
              Thêm thủ công
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            <div className="text-sm">⚠️ {error}</div>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
            <div className="text-sm">✓ {successMessage}</div>
          </div>
        )}

        {activeTab === 'folders' ? (
          <div className="grid grid-cols-1 lg:grid-cols-[360px,1fr] gap-6">
            <div className="bg-white rounded-lg p-4 shadow-sm space-y-4 h-fit">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <FolderPlus className="w-4 h-4" />
                {editingFolderId ? t('editFolder') : t('createFolder')}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">{t('folderName')}</label>
                <Input value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder={t('folderNamePlaceholder')} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">{t('description')}</label>
                <textarea
                  className="w-full min-h-[96px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  value={folderDescription}
                  onChange={(e) => setFolderDescription(e.target.value)}
                  placeholder={t('descriptionPlaceholder')}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleSaveFolder} disabled={savingFolder}>
                  {savingFolder ? t('savingFolder') : editingFolderId ? t('updateFolder') : t('createFolder')}
                </Button>
                {editingFolderId && (
                  <Button variant="ghost" onClick={resetFolderForm}>
                    {t('cancelEdit')}
                  </Button>
                )}
              </div>
              <div className="text-xs text-gray-500">
                {t('defaultFolderNote')}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {foldersLoading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader className="w-6 h-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">{t('loadingFolders')}</span>
                </div>
              ) : folders.length === 0 ? (
                <div className="p-8 text-center text-gray-500">{t('noFolders')}</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {folders.map((folder) => (
                    <div key={folder._id} className="p-4 flex items-start justify-between gap-4 hover:bg-gray-50">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-gray-900">{folder.name}</div>
                          {folder.is_default && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{t('isDefault')}</span>
                          )}
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{folder.status}</span>
                        </div>
                        {folder.description && (
                          <div className="text-sm text-gray-600 mt-1">{folder.description}</div>
                        )}
                        <div className="text-xs text-gray-500 mt-2">
                          {t('createdAt', { date: new Date(folder.created_at).toLocaleString() })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => handleEditFolder(folder)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                          <span className="text-xs">{t('editBtn')}</span>
                        </button>
                        {!folder.is_default && (
                          <button
                            onClick={() => handleDeleteFolder(folder)}
                            className="inline-flex items-center gap-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-xs">{t('deleteFolder')}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
        <>
        <div className="bg-white rounded-lg p-4 shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">{t('account')}</label>
              {accountsLoading ? (
                <div className="text-xs text-gray-500 italic px-2 py-2">{t('loading')}</div>
              ) : (
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  value={selectedAccountId}
                  onChange={(e) => {
                    setSelectedAccountId(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">{t('allAccounts')}</option>
                  {accounts.map((account) => (
                    <option key={account.account_id} value={account.account_id}>
                      {account.name || account.account_id}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">{t('tabFolders')}</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                value={selectedFolderId}
                onChange={(e) => {
                  setSelectedFolderId(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">{t('allFolders')}</option>
                {folders.map((folder) => (
                  <option key={folder._id} value={folder._id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">{t('search', { ns: 'common' })}</label>
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">{t('pageSize')}</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader className="w-6 h-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">{t('loading')}</span>
            </div>
          ) : contents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('noContents')}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('postUrl')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('account')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('folder')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Source</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('collectedAt')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('status')}</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">{t('delete', { ns: 'common' })}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contents.map((item) => {
                      const isDeleting = deleting.has(item._id);
                      return (
                        <tr key={item._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex flex-col max-w-xs">
                              <a
                                href={item.post_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all"
                                title={item.post_url}
                              >
                                {item.post_url.length > 50
                                  ? item.post_url.substring(0, 50) + '...'
                                  : item.post_url}
                              </a>
                              {item.content_snippet && (
                                <div className="text-xs text-gray-500 mt-1 break-words">
                                  "{item.content_snippet.substring(0, 40)}..."
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {item.account_id ? (
                              <span className="text-gray-700 font-mono text-xs">
                                {item.account_id.length > 12 ? `${item.account_id.substring(0, 12)}...` : item.account_id}
                              </span>
                            ) : item.fanpage_page_id ? (
                              <div className="flex flex-col gap-1">
                                <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 w-fit">
                                  Fanpage
                                </span>
                                <span className="text-gray-700 font-mono text-xs">
                                  {item.fanpage_page_id}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">Unknown</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                              {folderNameById[item.folder_id || ''] || 'Default'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                item.source === 'manual'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {item.source === 'manual' ? 'Manual' : 'Worker'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {new Date(item.created_at).toLocaleDateString()} {' '}
                            <span className="text-xs text-gray-500">
                              {new Date(item.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                item.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {item.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleDeleteItem(item._id)}
                              disabled={isDeleting}
                              className="inline-flex items-center gap-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Delete this seeding content"
                            >
                              {isDeleting ? (
                                <>
                                  <Loader className="w-4 h-4 animate-spin" />
                                  <span className="text-xs">Deleting...</span>
                                </>
                              ) : (
                                <>
                                  <Trash2 className="w-4 h-4" />
                                  <span className="text-xs">Delete</span>
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex} to {endIndex} of {total} items
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Previous page"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>

                    <span className="text-sm font-semibold text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Next page"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </>
        )}
      </div>

      {/* Manual Add Content Modal */}
      {showAddContentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Thêm seeding content thủ công</h3>
              <button onClick={() => { setShowAddContentModal(false); setAddContentError(null); }} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              {addContentError && <div className="text-xs text-red-500 bg-red-50 p-2 rounded-lg">{addContentError}</div>}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">URL bài đăng <span className="text-red-500">*</span></label>
                <input type="url" value={addContentForm.post_url} onChange={e => setAddContentForm(f => ({ ...f, post_url: e.target.value }))} placeholder="https://..." className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tg-primary/30 focus:border-tg-primary" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nền tảng</label>
                <select value={addContentForm.platform} onChange={e => setAddContentForm(f => ({ ...f, platform: e.target.value as 'facebook' | 'instagram' | 'tiktok' | 'linkedin' }))} className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tg-primary/30 focus:border-tg-primary">
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Loại bài</label>
                <select value={addContentForm.post_type} onChange={e => setAddContentForm(f => ({ ...f, post_type: e.target.value as 'group' | 'profile' | 'fanpage' }))} className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tg-primary/30 focus:border-tg-primary">
                  <option value="group">Nhóm</option>
                  <option value="profile">Trang cá nhân</option>
                  <option value="fanpage">Fanpage</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">URL mục tiêu</label>
                <input type="url" value={addContentForm.target_url} onChange={e => setAddContentForm(f => ({ ...f, target_url: e.target.value }))} placeholder="https://..." className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tg-primary/30 focus:border-tg-primary" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nội dung tóm tắt</label>
                <textarea value={addContentForm.content_snippet} onChange={e => setAddContentForm(f => ({ ...f, content_snippet: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tg-primary/30 focus:border-tg-primary resize-none" placeholder="Đoạn nội dung..." />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
              <button onClick={() => { setShowAddContentModal(false); setAddContentError(null); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Hủy</button>
              <button onClick={handleManualAddContent} disabled={addContentLoading} className="px-4 py-2 text-sm bg-tg-primary hover:bg-tg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50">{addContentLoading ? 'Đang lưu...' : 'Thêm content'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeedingContentsManagement;
